/**
 * The pill line's display contract (T-5 truncation hierarchy): one line split
 * at its truncation joints so `LivePill` can let the event NAME ellipsize
 * while the status and counts stay visible (Dexter's device-round ruling:
 * a long name must never eat the counts; when both counts can't fit, going
 * yields before posts).
 *
 * Pure module (no React) so the copy composer (`liveHomeCopy.ts`) and its
 * byte-exact string tests depend on the contract without pulling component
 * modules into the graph.
 */
import type { UserPublicRead } from '@models/api';

/**
 * The pill v2 goer stack's data (recap/pill redesign §1): the faces that
 * VISUALLY replace the live "is live · {n} going" text (the red dot carries
 * "live"; the faces carry "who"). Display-layer data, not copy — the joined
 * a11y line still comes from the string segments below, so assistive tech
 * keeps the full information whatever the visual layer compresses.
 * Composed per surface by `composePillGoers` (live-only; soon/ended → null).
 */
export interface LivePillGoers {
  /**
   * `event.preview_users` straight through (creator/host first, variable
   * length — ≤4 guest / ≤5 authed, NEVER a fixed size; absent on
   * probe-sourced payloads, where the pill falls back to the text form).
   */
  users: UserPublicRead[] | undefined;
  /** The full going-set size (`getEventGoingCount`) — the +N chip's total. */
  totalCount: number;
}

export interface LivePillSegments {
  /** The event name — the ONLY segment allowed to truncate (ellipsis). */
  name: string;
  /**
   * Phase status including its leading space (` is live` / ` starts in 2h`).
   * Always visible.
   */
  status: string;
  /**
   * Going count including its ` · ` separator (` · 2 going`,
   * ` · Marta +11 going`), or null when the variant has none. Hides on tight
   * rows ONLY when a posts segment is present to take over — as the pill's
   * only count it always stays.
   */
  going: string | null;
  /**
   * Posts count including its ` · ` separator (` · 8 posts`), or null when
   * gated out (live-only, counts ≥ 1). Never hidden — it outranks going
   * when space runs out.
   */
  posts: string | null;
  /**
   * True while the per-viewer posts count is still being fetched (the authed
   * first load of the surface's feed-counts batch, T-8). `LivePill` renders a
   * small skeleton block in the posts slot instead of copy. Never true
   * alongside a non-null `posts`, contributes nothing to the joined a11y
   * line, and — load-bearing — it does NOT count as "posts takes over" for
   * the tight-row going hide: a pending count may resolve to 0, and going
   * must never have vanished for a count that then shows nothing.
   */
  postsPending?: boolean;
}

/**
 * The one-line §4 copy a segment set renders as. Segments carry their own
 * leading separators, so the join is pure concatenation — this string is the
 * pill's accessible line (screen readers always get the full information,
 * whatever the visual layer truncates or hides) and the composed-copy test
 * surface. A pending posts skeleton (`postsPending`) is deliberately absent
 * here: an unresolved count is not information, so assistive tech never
 * hears a loading artifact.
 */
export const joinPillSegments = ({ name, status, going, posts }: LivePillSegments): string =>
  `${name}${status}${going ?? ''}${posts ?? ''}`;
